import { FunctionComponent, useCallback, useState } from 'react';
import { Button } from '@chakra-ui/react';

import Heading from 'components/_core/Heading';
import useToast from 'components/ToastProvider/useToast';

import useCompanyContext from '../../../../../../../../common/hooks/useCompanyContext';
import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from '../../../../../../../../common/hooks/useErrorHandledMutation';
import useSendCompanyInvitation from '../../../../../../common/useSendCompanyInvitation';
import {
  BeneficiaryFormWithEmail,
  formatCompanyBeneficiaryToBeneficiaryFormWithEmailInput,
} from '../../../../../../components/BeneficiaryForm';
import { BeneficiarySchema } from '../../../../../../components/BeneficiaryForm/beneficiarySchema';
import type { CurrentBeneficiariesEmails } from '../../../../../../components/BeneficiaryForm/helpers';
import {
  beneficiariesDocsStatusQuery,
  createPhysicalPersonLegalBeneficiaryMutation,
} from '../../../../../../legacy/Pages/BeneficiariesDocs/graphql';
import {
  CreatePhysicalPersonLegalBeneficiaryData,
  CreatePhysicalPersonLegalBeneficiaryInput,
} from '../../../../../../legacy/Pages/BeneficiariesDocs/types';
import locales from './locales';

interface CreateBeneficiaryProps {
  onDone: () => void;
  currentBeneficiariesEmails: CurrentBeneficiariesEmails;
}

const CreateBeneficiary: FunctionComponent<CreateBeneficiaryProps> = ({
  currentBeneficiariesEmails,
  onDone,
}) => {
  const { companyProfileId } = useCompanyContext();
  const { showToast } = useToast();

  const [isSending, setIsSending] = useState(false);

  const { sendCompanyInvitation } = useSendCompanyInvitation({
    awaitRefetchQueries: true,
    refetchQueries: () => [
      {
        query: beneficiariesDocsStatusQuery,
        variables: {
          companyProfileId,
        },
      },
    ],
  });

  const [mutator] = useErrorHandledMutation<
    CreatePhysicalPersonLegalBeneficiaryData,
    CreatePhysicalPersonLegalBeneficiaryInput
  >(createPhysicalPersonLegalBeneficiaryMutation);

  const createLegalBeneficiary = useCallback(
    async (formValues: BeneficiarySchema) => {
      if (companyProfileId === undefined) {
        throw new Error('companyProfileId should be defined');
      }

      setIsSending(true);

      const result = await mutator({
        variables: {
          input: {
            companyProfileId,
            ...formValues,
          },
        },
      }).catch(throwOnlyUnhandledErrors);

      if (result && result.data) {
        const {
          data: {
            createPhysicalPersonLegalBeneficiary: {
              invitation: { companyInvitationId },
            },
          },
        } = result;
        await sendCompanyInvitation({
          companyInvitationId,
          companyProfileId,
        });
      }

      showToast({
        message: `${locales.formatString(
          formValues.gender === 'male'
            ? locales.addedMaleBeneficiary
            : locales.addedFemaleBeneficiary,
          {
            name: `${formValues.firstName} ${formValues.lastName}`,
          },
        )}`,
        type: 'success',
      });

      setIsSending(false);
      onDone();
    },
    [companyProfileId, onDone, mutator, sendCompanyInvitation, showToast],
  );

  return (
    <>
      <Heading marginBottom="space-48" size="lg">
        {locales.title}
      </Heading>
      <BeneficiaryFormWithEmail
        beneficiary={formatCompanyBeneficiaryToBeneficiaryFormWithEmailInput(
          null,
        )}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        primaryAction={{
          isSending,
          onSubmit: createLegalBeneficiary,
          title: locales.cta,
        }}
        roleWarningCalloutContext="create"
        secondaryAction={
          <Button onClick={onDone} variant="secondary">
            {locales.cancel}
          </Button>
        }
      />
    </>
  );
};

export default CreateBeneficiary;
